import { useTranslation } from "react-i18next";
import {
  PrimaryButtonMedium,
  SecondaryButtonMedium,
} from "../../../../components/Buttons/Buttons";
import type { AxiosError } from "axios";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import styled from "styled-components/macro";
import {
  H3,
  H6Bold,
  SmallSectionHeaderRegular,
} from "../../../../components/Typography/Typography";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import type { OptionType } from "../../../../types/types";
import useSWR from "swr";
import { useStoreState } from "../../../../util/util";
import {
  convertArrayBufferToZipUrl,
  DocumentDownloadContainer,
  RegularTextBlock,
  TemplateButtonContainer,
} from "../../OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/createProductFromUploads.util";
import { PreviousPriceTiersUploadTable } from "./PreviousPriceTiersUploadTable";
import { endpoints } from "../../../../endpoints";
import axios from "axios";

interface DownloadPriceTiersProps {
  goBack: () => void;
  setStage: Dispatch<SetStateAction<"upload" | "download">>;
  price_tiers_csv_id?: string;
}

const DownloadPriceTiersContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 32px 0 22px;
`;

const Section = styled.section`
  display: flex;
  gap: 4px;
  flex-direction: column;
  margin: 12px 0;
`;

/**
 * DownloadPriceTiers component
 * This component handles the download of price tiers template for bulk upload.
 *
 * @param {DownloadPriceTiersProps} props - Component props
 * @returns JSX.Element
 */
export function DownloadPriceTiers({
  goBack,
  setStage,
  price_tiers_csv_id,
}: DownloadPriceTiersProps): JSX.Element {
  const [priceTiers, setPriceTiers] = useState<OptionType<string>>();
  const [priceTierError, setPriceTierError] = useState<boolean>(false);
  const [products, setProducts] = useState<OptionType<string>>();
  const [customers, setCustomers] = useState<OptionType<string>>();
  const [distributors, setDistributors] = useState<OptionType<string>>();
  const { storefront_id, slug } = useStoreState();
  const { t } = useTranslation();
  useNotifications();

  useSWR<Blob | null, AxiosError>(
    `/v2/storefronts/${storefront_id}/price-tiers/bulk-upload/product-sku-data`,
    {
      revalidateOnFocus: false,
      onSuccess: (data) =>
        data &&
        setProducts({
          label: `${slug}_products`,
          value: convertArrayBufferToZipUrl(
            new Blob([String.fromCharCode(0xfeff), data], {
              type: "text/csv",
            })
          ),
        }),
      onError: (_error) => {
        setProducts(undefined);
      },
    }
  );

  useSWR<Blob | null, AxiosError>(
    [
      endpoints.v1_storefronts_id_customer_reports_fileType(
        storefront_id,
        "csv"
      ),
      useMemo(
        () => ({
          params: {
            timezone_iana_zoneinfo:
              Intl.DateTimeFormat().resolvedOptions().timeZone,
            include_uuids: true,
          },
          responseType: "blob",
        }),
        []
      ),
    ],
    {
      revalidateOnFocus: false,
      onSuccess: (data) =>
        data &&
        setCustomers({
          label: `${slug}_customers`,
          value: convertArrayBufferToZipUrl(
            new Blob([String.fromCharCode(0xfeff), data], {
              type: "text/csv",
            })
          ),
        }),
      onError: (_error) => {
        setCustomers(undefined);
      },
    }
  );

  useSWR<Blob | null, AxiosError>(
    [
      endpoints.v1_storefronts_id_distributor_reports_fileType(
        storefront_id,
        "csv"
      ),
      useMemo(
        () => ({
          params: {
            timezone_iana_zoneinfo:
              Intl.DateTimeFormat().resolvedOptions().timeZone,
            include_uuids: true,
          },
          responseType: "blob",
        }),
        []
      ),
    ],
    {
      revalidateOnFocus: false,
      onSuccess: (data) =>
        data &&
        setDistributors({
          label: `${slug}_distributors`,
          value: convertArrayBufferToZipUrl(
            new Blob([String.fromCharCode(0xfeff), data], {
              type: "text/csv",
            })
          ),
        }),
      onError: (_error) => {
        setDistributors(undefined);
      },
    }
  );

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const pollPriceTiersData = async (jobId: string) => {
      try {
        const response = await axios.get<{
          job_id: string;
          status: string;
          message: string;
          download_url?: string;
        }>(
          `/v2/storefronts/${storefront_id}/price-tiers/bulk-upload/template/status/${jobId}`,
          { responseType: "json" }
        );

        if (!isMounted) return;

        const responseData = response.data;
        if ("status" in responseData) {
          if (
            responseData.status === "completed" &&
            responseData.download_url
          ) {
            setPriceTiers({
              label: `${slug}_price_tiers`,
              value: responseData.download_url,
            });
          } else if (responseData.status === "processing") {
            // Still processing, poll again in 2 seconds
            timeoutId = setTimeout(() => pollPriceTiersData(jobId), 2000);
          } else {
            setPriceTierError(true);
          }
        } else {
          setPriceTierError(true);
        }
      } catch (error) {
        if (!isMounted) return;
        setPriceTierError(true);
      }
    };

    if (price_tiers_csv_id && !priceTiers && !priceTierError) {
      pollPriceTiersData(price_tiers_csv_id);
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [price_tiers_csv_id, priceTiers, priceTierError, storefront_id, slug]);

  return (
    <DownloadPriceTiersContainer>
      <Section>
        <H3 style={{ margin: 0 }}>{t("Download Price Tier Data")}</H3>
        <SmallSectionHeaderRegular style={{ margin: 0 }}>
          {t("Download existing price tiers and customer data")}
        </SmallSectionHeaderRegular>
      </Section>
      {priceTiers && priceTiers.value && (
        <Section>
          <H6Bold style={{ margin: 0 }}>{t("Price Tiers")}</H6Bold>
          <RegularTextBlock>
            {t("Download existing price tiers")}
          </RegularTextBlock>
          <div>
            <DocumentDownloadContainer
              type="text/csv"
              link={priceTiers.value}
              documentName={priceTiers.label}
            />
          </div>
        </Section>
      )}
      {(!!products || !!customers || !!distributors) && (
        <Section>
          <H6Bold style={{ margin: 0 }}>{t("Supporting Files")}</H6Bold>
          <RegularTextBlock>
            {t("Download additional data used in price tiers. Optional.")}
          </RegularTextBlock>
          {products && (
            <DocumentDownloadContainer
              type="text/csv"
              link={products.value}
              documentName={products.label}
            />
          )}
          {customers && (
            <DocumentDownloadContainer
              type="text/csv"
              link={customers.value}
              documentName={customers.label}
            />
          )}
          {distributors && (
            <DocumentDownloadContainer
              type="text/csv"
              link={distributors.value}
              documentName={distributors.label}
            />
          )}
        </Section>
      )}
      <TemplateButtonContainer style={{ marginTop: "24px" }}>
        <SecondaryButtonMedium onClick={goBack}>
          {t("Back")}
        </SecondaryButtonMedium>
        <PrimaryButtonMedium
          disabled={!priceTiers}
          onClick={() => setStage("upload")}
        >
          {t("Continue")}
        </PrimaryButtonMedium>
      </TemplateButtonContainer>
      <div style={{ margin: "24px 0" }}>
        <PreviousPriceTiersUploadTable />
      </div>
    </DownloadPriceTiersContainer>
  );
}
